import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Locale} from "data/enums";
import {Bindings} from "data/constants/bindings";
import {makeAutoObservable, runInAction, reaction} from "mobx";

import type {IPlayersStore} from "data/stores/players/players.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IHttpClientService} from "data/services/http";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {trackSentryErrors} from "data/utils";
import type {IChecksumStore} from "data/stores/checksum/checksum.store";
import {AdobeDTM} from "data/utils/AdobeDTM";

export interface IBootstrapController extends ViewController {
	get isReady(): boolean;
}

@injectable()
export class BootstrapController implements IBootstrapController {
	private _userLocale: Locale = navigator.language as Locale;
	private _isReady = false;
	private _disposers: ReturnType<typeof reaction>[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ApiHTTPClient) private _apiHTTPClient: IHttpClientService,
		@inject(Bindings.GlobalApiHTTPClient) private _globalApiHTTPClient: IHttpClientService,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.ChecksumStore) private _checksums: IChecksumStore,
		@inject(Bindings.ContestsStore) private _contestStore: IContestsStore,
		@inject(Bindings.TournamentsStore) private _tournamentsStore: ITournamentsStore
	) {
		makeAutoObservable(this);
	}

	get isReady(): boolean {
		return this._isReady;
	}

	/**
	 * The method is to define a user's locale. It can be done by:
	 * 1) navigator.language
	 * 2) Site URL
	 * 3) Some JSON or API request settings
	 * 4) Whatever else
	 */
	private async defineLocale(): Promise<Locale> {
		this._userLocale = await Promise.resolve(Locale.EN_US);
		return this._userLocale;
	}

	async init() {
		try {
			await Promise.all([
				this._i18nStore.switchLocale({
					locale: await this.defineLocale(),
				}),
				this._contestStore.fetchContests(),
				this._playersStore.fetchPlayers(),
				this._squadsStore.fetchSquads(),
				this._roundsStore.fetchRounds(),
				this._tournamentsStore.fetchTournaments(),
			]);

			this._disposers = [
				reaction(
					() => this._contestStore.currentGameContest,
					(contest) => {
						const contestId = contest?.id;
						if (contestId) {
							this._checksums.setLiveContestId(contestId);
						}
					},
					{fireImmediately: true}
				),
				reaction(
					() => this._roundsStore.activeRound,
					(round) => {
						const roundId = round?.id;
						if (roundId) {
							this._checksums.setLiveRoundId(roundId);
						}
					},
					{fireImmediately: true}
				),
			];
		} catch (_err) {
			trackSentryErrors(_err, {}, "init");
			// Show error message to a user
		}

		// Set locale that will be appended to each request
		this._apiHTTPClient.setLocale(this._i18nStore.lang);
		this._globalApiHTTPClient.setLocale("en");

		runInAction(() => {
			this._isReady = true;
		});

		AdobeDTM.trackState({
			"page.legacyName": "Major League Baseball: Spinball",
		});
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}
}
